/**
 * Авто-декорирование «голых» партнёрских ссылок в контенте (блог-посты).
 *
 * Дополняет точечные декораторы trip/booking/tripster: если автор вставил
 * обычную ссылку на партнёра query-param-типа (Viator, yesim, GetYourGuide,
 * Kiwitaxi) без партнёрской метки — дописываем её. Метки совпадают с канонами
 * статических билдеров (см. builders/viator.js, getYourGuide.js, kiwitaxi.js,
 * провайдер yesim в Sanity).
 *
 * Партнёры с непрозрачными трекинг-ссылками (airalo.com → Impact, saily.com →
 * go.saily.site, localrent → /l/<hash>/) сюда НЕ попадают: их нельзя восстановить
 * из голого домена параметром — нужна замена URL на трекер-ссылку вручную.
 */

import { shouldSkipUrl } from './trip';

const VIATOR_PARAMS = { pid: 'P00252934', mcid: '42383', medium: 'link' };
const YESIM_PARTNER_ID = '1197';
const GYG_PARAMS = { partner_id: '8NCOLVX', utm_medium: 'online_publisher' };
const KIWITAXI_PAP = '682dafaa93bea';
// Id партнёра Localrent. Полные ссылки /<lang>/<country>/ без r — клик без партнёра: так
// стояли ссылки в блоке «Источники» (краул 29.09.2026). Короткие /l/<hash>/ не трогаем —
// у них партнёр зашит, а marker они всё равно теряют (CLAUDE.md).
const LOCALRENT_PARTNER_ID = '11074';

const isHost = (host, name) => host === name || host === `www.${name}`;

/**
 * @param {string} href - исходный URL из контента
 * @param {string|null} [language] - зарезервировано (для будущих доменов по языку)
 * @returns {string} URL с партнёрской меткой либо исходный href без изменений
 */
export function decorateGenericAffiliateUrl(href, _language = null) {
    if (!href || typeof href !== 'string') return href;
    // tp.media / travelpayouts уже партнёрские — не трогаем
    if (shouldSkipUrl(href)) return href;

    let url;
    try {
        url = new URL(href);
    } catch {
        return href;
    }
    const host = url.hostname.toLowerCase();

    if (isHost(host, 'viator.com')) {
        for (const [k, v] of Object.entries(VIATOR_PARAMS)) url.searchParams.set(k, v);
        return url.toString();
    }
    if (host === 'yesim.app' || host === 'yesim.tech') {
        url.searchParams.set('partner_id', YESIM_PARTNER_ID);
        return url.toString();
    }
    if (isHost(host, 'getyourguide.com') || isHost(host, 'getyourguide.ru')) {
        for (const [k, v] of Object.entries(GYG_PARAMS)) url.searchParams.set(k, v);
        return url.toString();
    }
    if (isHost(host, 'kiwitaxi.ru') || isHost(host, 'kiwitaxi.com')) {
        url.searchParams.set('pap', KIWITAXI_PAP);
        return url.toString();
    }
    if (isHost(host, 'localrent.com') && !url.pathname.startsWith('/l/')) {
        if (url.searchParams.get('r') === LOCALRENT_PARTNER_ID) return href;
        url.searchParams.set('r', LOCALRENT_PARTNER_ID);
        return url.toString();
    }

    return href;
}
